// Benchmarking Database — aggregate anonymous data from all audits
import { supabase } from './supabase';

// Submit anonymous benchmark data after an audit is scored
export async function submitBenchmark(audit) {
  if (!audit?.scores || !audit?.intake?.city) return;

  const scores = {};
  Object.entries(audit.scores).forEach(([key, val]) => {
    if (val?.score > 0) scores[key] = val.score;
  });

  if (Object.keys(scores).length < 5) return; // Need at least 5 pillars scored

  const benchmark = {
    id: `bm_${audit.id}`,
    concept_type: detectConceptType(audit.intake?.conceptName || ''),
    city: audit.intake.city,
    market: extractMarket(audit.intake.city),
    scores,
    next_index: Object.values(scores).reduce((s, v) => s + v, 0) / Object.values(scores).length,
    created_at: new Date().toISOString(),
  };

  try {
    await supabase.from('benchmarks').upsert(benchmark, { onConflict: 'id' });
  } catch (e) {
    console.warn('Benchmark submit failed:', e.message);
  }
}

// Get benchmark averages for a market/concept type
export async function getBenchmarks(city, conceptType) {
  try {
    const market = extractMarket(city);
    const { data, error } = await supabase
      .from('benchmarks')
      .select('scores, next_index, concept_type, market')
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) throw error;
    if (!data || data.length === 0) return null;

    // Calculate averages
    const allBenchmarks = data;
    const marketBenchmarks = data.filter(b => b.market === market);
    const typeBenchmarks = conceptType ? data.filter(b => b.concept_type === conceptType) : [];

    return {
      total: allBenchmarks.length,
      all: calculateAverages(allBenchmarks),
      market: marketBenchmarks.length >= 3 ? calculateAverages(marketBenchmarks) : null,
      marketCount: marketBenchmarks.length,
      type: typeBenchmarks.length >= 3 ? calculateAverages(typeBenchmarks) : null,
      typeCount: typeBenchmarks.length,
    };
  } catch (e) {
    console.warn('Benchmark fetch failed:', e.message);
    return null;
  }
}

function calculateAverages(benchmarks) {
  if (benchmarks.length === 0) return null;

  const pillarSums = {};
  const pillarCounts = {};
  let indexSum = 0;

  benchmarks.forEach(b => {
    indexSum += b.next_index || 0;
    Object.entries(b.scores || {}).forEach(([key, val]) => {
      if (!pillarSums[key]) { pillarSums[key] = 0; pillarCounts[key] = 0; }
      pillarSums[key] += val;
      pillarCounts[key]++;
    });
  });

  const avgScores = {};
  Object.keys(pillarSums).forEach(key => {
    avgScores[key] = Math.round((pillarSums[key] / pillarCounts[key]) * 10) / 10;
  });

  return {
    avgIndex: Math.round((indexSum / benchmarks.length) * 10) / 10,
    avgScores,
    count: benchmarks.length,
  };
}

function extractMarket(city) {
  if (!city) return 'unknown';
  // Extract state/region from city string like "Philadelphia, PA"
  const parts = city.split(',').map(s => s.trim());
  return parts[parts.length - 1]?.toLowerCase() || city.toLowerCase();
}

function detectConceptType(name) {
  const lower = name.toLowerCase();
  if (lower.includes('bar') || lower.includes('lounge') || lower.includes('speakeasy') || lower.includes('cocktail')) return 'bar';
  if (lower.includes('cafe') || lower.includes('coffee')) return 'cafe';
  if (lower.includes('hotel') || lower.includes('resort') || lower.includes('inn')) return 'hotel_fb';
  if (lower.includes('steakhouse') || lower.includes('grill')) return 'steakhouse';
  if (lower.includes('pizza') || lower.includes('taco') || lower.includes('burger')) return 'casual';
  return 'restaurant';
}
